import { Form, useFormContext } from "@/components/form";
import { CollapsibleSection } from "@/components/form/collapsible-section";
import {
    Stack,
    TextField,
    InputAdornment,
    IconButton
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { Dialog, ModalContextExtractor, ModalHeader, useOpenModal } from "@/components/modal";
import type { Cliente } from "@/shared/types/cliente";
import { getClienteDisplayValue } from "../../utils/de-data-utils";
import ClienteInfoCard from "../cliente-info-card";
import ClienteManualSearch from "@/features/facturacion-electronica/components/cliente-manual-search";

interface NotaCreditoGeneralInfoFieldsProps {
    nextInputRef?: React.MutableRefObject<any>;
    detalleFormRef?: React.MutableRefObject<any>;
    onClienteChange?: (cliente: Cliente | null) => void;
    cliente?: Cliente | null;
    clienteError?: boolean;
    cdcLoaded?: boolean;
}

export default function NotaCreditoGeneralInfoFields({
    onClienteChange,
    cliente,
    clienteError = false,
    cdcLoaded
}: NotaCreditoGeneralInfoFieldsProps) {
    const openModal = useOpenModal();
    const form = useFormContext();

    // Selección de cliente desde el modal de búsqueda
    const handleClienteSelect = (cliente: Cliente) => {
        if (cliente.id) {
            form.setFieldValue("clienteId", cliente.id);
        } else {
            form.setFieldValue("clienteId", "");
        }
        if (onClienteChange) {
            onClienteChange(cliente);
        }
    };

    const handleClearCliente = () => {
        form.setFieldValue("clienteId", "");
        if (onClienteChange) {
            onClienteChange(null);
        }
    };

    return (
        <CollapsibleSection title="Información General">
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems="flex-start" flexWrap="wrap">
                <Stack flex={{ xs: '1 1 100%', sm: '2 1 350px' }} minWidth="280px">
                    <TextField
                        label="Cliente (Receptor) *"
                        size="small"
                        fullWidth
                        value={getClienteDisplayValue(cliente)}
                        placeholder="Buscar cliente..."
                        error={clienteError && !cliente}
                        helperText={clienteError && !cliente ? "Cliente es requerido" : ""}
                        slotProps={{
                            input: {
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {cliente && (
                                            <IconButton size="small" onClick={handleClearCliente} disabled={cdcLoaded}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        <IconButton size="small" onClick={() => openModal("searchCliente")} disabled={cdcLoaded}>
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }
                        }}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => !cliente && openModal("searchCliente")}
                        disabled={cdcLoaded}
                    />
                    <ClienteInfoCard cliente={cliente || null} title="Receptor" />
                </Stack>

                <Stack flex={{ xs: '1 1 100%', sm: '1 1 250px' }} minWidth="200px">
                    <Form.Select name="motivoEmision" label="Motivo de Emisión *" size="small">
                        <Form.Select.Option value="1">Devolución</Form.Select.Option>
                        <Form.Select.Option value="2">Descuento</Form.Select.Option>
                        <Form.Select.Option value="3">Bonificación</Form.Select.Option>
                        <Form.Select.Option value="4">Corrección</Form.Select.Option>
                        <Form.Select.Option value="5">Anulación</Form.Select.Option>
                    </Form.Select>
                </Stack>

                <Stack flex={{ xs: '1 1 100%', sm: '1 1 250px' }} minWidth="200px">
                    <Form.TextField name="observaciones" label="Observaciones" size="small" fullWidth />
                </Stack>
                <Form.TextField name="cMoneOpe" label="Moneda de Operación" size="small" disabled />
            </Stack>

            <Dialog code="searchCliente" size="lg">
                <ModalHeader>Buscar Cliente</ModalHeader>
                <ModalContextExtractor>
                    {({ handleClose }) => (
                        <ClienteManualSearch
                            selectedClienteId={cliente?.id}
                            onClienteSelect={(cliente: Cliente) => {
                                handleClienteSelect(cliente);
                                handleClose();
                            }}
                        />
                    )}
                </ModalContextExtractor>
            </Dialog>
        </CollapsibleSection>
    );
}
